import { fg } from '@atlaskit/platform-feature-flags/fg';

import { type EditionAwarenessContextType } from './edition-awareness-context';
import { isEditionOneOf } from './is-edition-one-of';

/**
 * Jira Software Standard sites that are at or over their attachment storage allowance
 * (JSSHL, TAIYO-388).
 *
 * Sibling of `isEligibleForStorageHardLimit`, which is Free-only and fully rolled out. This one
 * deliberately reads no entitlement-derived field (`canUpgrade`, `canDirectBuyUpgrade`,
 * `BILLING_ADMIN`): a paid, non-trialling Standard tenant resolves a null entitlement under the
 * root query's filter, so those are never available here. `isInCollection` is `UNKNOWN` for the
 * same reason, so `UNKNOWN` is accepted alongside `NOT_IN_COLLECTION`; only `IN_COLLECTION`
 * excludes.
 *
 * That exclusion is role-dependent: the root query only asks for COLLECTION relationships on behalf
 * of site admins, so on a collection tenant an admin resolves `IN_COLLECTION` (excluded here, and
 * handled by the collection branches above this one) while other users resolve `UNKNOWN` and are
 * eligible. Making it tenant-level needs the entitlement to resolve for every user, which is a
 * root-query change outside TAIYO-388.
 *
 * Does not fire exposure. The experiment cohort is read where the dialog is dispatched.
 */
export function isEligibleForJiraStandardStorageHardLimit(
	context: EditionAwarenessContextType,
): boolean {
	const {
		product,
		edition,
		isInTrial,
		isInPredunning,
		isInCollection,
		storageUsageInGB,
		storageLimitInGB,
		storageIsUnlimited,
	} = context;

	if (!storageUsageInGB || !storageLimitInGB) {
		return false;
	}

	return Boolean(
		product === 'jira' &&
		isEditionOneOf(edition, ['standard']) &&
		!isInTrial &&
		!isInPredunning &&
		(isInCollection === 'NOT_IN_COLLECTION' || isInCollection === 'UNKNOWN') &&
		!storageIsUnlimited &&
		storageUsageInGB >= storageLimitInGB &&
		fg('jira-standard-storage-hard-limits-kill-switch'),
	);
}
